import { CustomField } from "@/backend/models/customFields/customField";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { useTreeStore } from "@/frontend/store/TreeStore";
import { I18n } from "@/i18n/I18n";
import { ParameterInputType, ParameterSubjectType, ParameterType, SelectableParameter } from "@/types/parameterTypes";
import { useState, useEffect } from "react";
import { Form, Modal } from "react-bootstrap";
import { useShallow } from "zustand/react/shallow";
import { GenericParameterSelect } from "../parameters/GenericParameterSelect";
import { Select } from "../Select";
import { BasicButton } from "./BasicButton";
import { Parameter } from "@/backend/models/parameter";
import { Container } from "@/backend/models/containers/container";

export function ContainerVisibilityButton(props: { item: Container }) {
  const { item } = props;
  const [showConditionalModal, setShowConditionalModal] = useState(false);
  const selectedParameter = useParameterStore(useShallow(state => state.parameters.find(p => p.fields.some(f => f.id === item.conditionalFieldId))));
  const selectedField = selectedParameter?.fields.find(f => f.id === item.conditionalFieldId);
  const updateItem = useTreeStore(state => state.updateContainer);

  if (selectedParameter && selectedField) {
    return (
      <div className="p-3 bg-success bg-opacity-10 rounded d-flex align-items-center justify-content-between">
        <small className="text-success">
          <strong>{I18n.get('containerWillShowWhen', { field: selectedField.getLabel(), parameter: selectedParameter.title, containerType: item.containerType })}</strong>
        </small>
        <BasicButton
          onClick={() => updateItem({ container: { ...item, conditionalFieldId: '' } })}
          label={I18n.get('removeVisibilityCondition')}
        />
      </div>
    );
  }

  return (
    <>
      <BasicButton
        onClick={() => setShowConditionalModal(true)}
        label={I18n.get('setContainerVisibility', { containerType: item.containerType })}
      />
      {showConditionalModal && <ConditionalModal setShow={setShowConditionalModal} item={item} />}
    </>
  );
}

function ConditionalModal(props: { setShow: (show: boolean) => void, item: Container }) {
  const { setShow, item  } = props;
  const parameters = useParameterStore(useShallow(state => state.parameters.filter(p => 
    p.inputType === ParameterInputType.MULTIPLE_CHOICE 
    && p.subjectType === ParameterSubjectType.PROCEDURE
    && p.type === ParameterType.CONFIG
  )));
  const [selectedParameter, setSelectedParameter] = useState<SelectableParameter | undefined>(undefined);
  const [selectedField, setSelectedField] = useState<CustomField | undefined>(undefined);
  const updateItem = useTreeStore(state => state.updateContainer);

  const fullParameter = useParameterStore(useShallow(state => state.parameters.find(p => p.id === selectedParameter?.id)));

  return (
    <Modal show onHide={() => setShow(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{I18n.get('configureContainerVisibility', { containerType: item.containerType })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <p className="text-muted small">{I18n.get('containerVisibilityDescription', { containerType: item.containerType })}</p>
        </div>

        <div className="row">
          <div className="col-md-6">
            <Form.Label className="col-form-label col-form-label-sm fw-bold">
              {I18n.get('selectParameterForVisibility', { containerType: item.containerType })}
            </Form.Label>
            <GenericParameterSelect
              parameters={parameters}
              currentParameter={selectedParameter}
              createReference={(param) => {
                setSelectedParameter(param);
                setSelectedField(undefined);
              }}
              deleteReference={() => {
                setSelectedParameter(undefined);
                setSelectedField(undefined);
              }}
            />
          </div>

          <div className="col-md-6">
            <Form.Label className="col-form-label col-form-label-sm fw-bold">
              {I18n.get('selectFieldForVisibility', { containerType: item.containerType })}
            </Form.Label>
            {selectedParameter && fullParameter ? (
              <FieldSelector
                parameter={fullParameter}
                selectedField={selectedField}
                setSelectedField={setSelectedField}
              />
            ) : (
              <div className="p-3 border rounded bg-light">
                <small className="text-muted">{I18n.get('selectAndInputFirst', { containerType: item.containerType })}</small>
              </div>
            )}
          </div>
        </div>
        {selectedField && selectedParameter && (
          <div className="mt-4 p-3 bg-success bg-opacity-10 rounded">
            <small className="text-success">
              <strong>{I18n.get('containerWillShowWhen', { field: selectedField.getLabel(), parameter: selectedParameter.title, containerType: item.containerType })}</strong>
            </small>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex gap-2">
          <BasicButton
            label={I18n.get('cancel')}
            onClick={() => setShow(false)}
          />
          <BasicButton
            label={I18n.get('saveVisibilityConfig')}
            onClick={() => {
              if (selectedField) {
                void updateItem({ container: { ...item, conditionalFieldId: selectedField.id } });
              }
              setShow(false);
            }}
            disabled={!selectedField}
            className='btn btn-primary'
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function FieldSelector(props: {
  parameter: Parameter,
  selectedField: CustomField | undefined,
  setSelectedField: (field: CustomField) => void
}) {
  const { parameter, selectedField, setSelectedField } = props;
  const [dropdownValue, setDropdownValue] = useState(selectedField?.getLabel() || I18n.get('none'));

  useEffect(() => {
    setDropdownValue(selectedField?.getLabel() || I18n.get('none'));
  }, [selectedField]);

  return (
    <div>
      <Select value={dropdownValue} label={I18n.get('selectField')}>
        {parameter.fields.map((field) => (
          <button
            type="button"
            key={field.id}
            className="w-100 dropdown-item d-flex align-items-center"
            onClick={() => {
              setDropdownValue(field.getLabel());
              setSelectedField(field);
            }}
          >
            {field.getLabel()}
          </button>
        ))}
      </Select>
    </div>
  );
}
